import { Controller } from '@hotwired/stimulus'
import delay from 'lodash/delay'
import { getCount } from '../../api/common/count'

const DELAY_TIME = 800

export default class extends Controller {
  static targets = [
    'facilitiesCount',
    'footer',
    'main',
    'searchQuestionForm',
    'question',
    'submit',
    'submitButton',
    'feeNote',
    'chatInputFeeNextButton',
    'medicalCareList',
    'medicalCareOther',
    'chatInputMedicalCareNextButton',
    'summaryAboutAdmissionFee',
    'summaryAboutCareLevel',
    'summaryAboutDementia',
    'summaryAboutDoubleRoom',
    'summaryAboutMonthlyFee',
    'summaryAboutWelfare',
    'resetButton',
  ]

  static classes = [
    'displayVisible',
    'displayHidden',
    'visibilityVisible',
  ]

  get controllerName () {
    return this.element.dataset.controller
  }

  get className () {
    return this.element.dataset.classname
  }

  connect () {
    this.setFacilitiesCount()
    this.showNextChat(1)
  }

  isElementInViewport (element) {
    const rect = element.getBoundingClientRect()
    return (
      rect.top >= 0 &&
      rect.left >= 0 &&
      rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
      rect.right <= (window.innerWidth || document.documentElement.clientWidth)
    )
  }

  hideCurrentChatInput (e, text) {
    const title = e.currentTarget.dataset.title
    const current = e.currentTarget.dataset.current
    const currentInput = document.getElementById(`chat_input_${current}`)
    currentInput.classList.add(this.displayHiddenClass)

    delay(() => {
      currentInput.insertAdjacentHTML('beforebegin', `
        <div class="${this.element.dataset.classname}::chat-answer-container">
        <div class="${this.element.dataset.classname}::chat-answer">${text || title}</div>
        </div>
      `)
    }, DELAY_TIME / 2)

    delay(() => {
      const target = document.getElementById(`step_${current}`).getElementsByClassName(`${this.element.dataset.classname}::chat-answer-container`)
      if (!this.isElementInViewport(target[0])) {
        window.scroll({ top: target[0].offsetTop + target[0].offsetHeight - window.innerHeight + 30, left: 0, behavior: 'smooth' })
      }
    }, DELAY_TIME / 2 + 100)
  }

  showNextChat (step, delayTime) {
    const chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
    const chatInput = document.getElementById(`chat_input_${step}`)
    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom = this.questionTargets[step - 1]

    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
      if (step === 7) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
        if (step !== 1) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2) - 400)
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
        if (index + 1 < chatQuestions.length) loaders[index + 1].classList.remove(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2))
      delay(() => {
        if (step !== 1) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 2))
    })
    if (delayTime) {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
      }, delayTime)
      delay(() => {
        if (step !== 1) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, delayTime + 400)
    } else {
      delay(() => {
        chatInput.classList.add(this.visibilityVisibleClass)
        if (step !== 1) window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (chatQuestions.length + 2))
    }
  }

  showNextChatSlow (step) {
    const DELAY_TIME = 1000
    const chatQuestionContainer = document.getElementById(`chat_question_container_${step}`)
    const chatInput = document.getElementById(`chat_input_${step}`)
    const loaders = chatQuestionContainer.getElementsByClassName(`${this.className}::loader`)
    const chatQuestions = chatQuestionContainer.getElementsByClassName(`${this.className}::chat-question`)
    const nextDom = this.questionTargets[step - 1]

    this.questionTargets[step - 1].style.display = 'block'
    delay(() => {
      chatQuestionContainer.classList.add(this.visibilityVisibleClass)
      window.scroll({ top: chatQuestionContainer.offsetTop + chatQuestionContainer.offsetHeight - window.innerHeight + 120, left: 0, behavior: 'smooth' })
    }, DELAY_TIME)
    Array.from(chatQuestions).forEach((elm, index) => {
      delay(() => {
        loaders[index].classList.add(this.displayHiddenClass)
      }, DELAY_TIME * (index + 2) - 400)
      delay(() => {
        chatQuestions[index].classList.add(this.displayVisibleClass)
      }, DELAY_TIME * (index + 2))
      delay(() => {
        window.scroll({ top: nextDom.offsetTop, left: 0, behavior: 'smooth' })
      }, DELAY_TIME * (index + 3))
    })
    delay(() => {
      chatInput.classList.add(this.visibilityVisibleClass)
    }, DELAY_TIME * (chatQuestions.length + 2))
    delay(() => {
      this.footerTarget.classList.add(this.displayVisibleClass)
    }, DELAY_TIME * (chatQuestions.length + 2) + 200)
  }

  nextChatWithScroll (e) {
    this.hideCurrentChatInput(e)
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  skipChatWithScroll (e) {
    this.hideCurrentChatInput(e)
    this.showNextChat(Number(e.currentTarget.dataset.next) + 1)
  }

  chatInputFeeNextButtonStatus (e) {
    const monthlyFeeMin = document.getElementById('monthly_fee_min')
    const monthlyFeeMax = document.getElementById('monthly_fee_max')
    const selectedValue1 = monthlyFeeMin.value
    const selectedValue2 = monthlyFeeMax.value

    const admissionFeeMin = document.getElementById('admission_fee_min')
    const admissionFeeMax = document.getElementById('admission_fee_max')
    const selectedValue3 = admissionFeeMin.value
    const selectedValue4 = admissionFeeMax.value

    let setButtonStatus

    if ((((selectedValue1 * 1 - selectedValue2 * 1) > 0 && !(selectedValue1 === '-1' || selectedValue2 === '-1'))) || ((selectedValue3 * 1 - selectedValue4 * 1) > 0 && !(selectedValue3 === '-1' || selectedValue4 === '-1'))) {
      setButtonStatus = true
      this.feeNoteTarget.style.display = 'block'
    } else if (selectedValue1 === '-1' && selectedValue2 === '-1' && selectedValue3 === '-1' && selectedValue4 === '-1') {
      setButtonStatus = true
      this.feeNoteTarget.style.display = 'none'
    } else {
      setButtonStatus = false
      this.feeNoteTarget.style.display = 'none'
    }

    this.chatInputFeeNextButtonTarget.disabled = setButtonStatus
  }

  chatInputMedicalCareNextButtonStatus (e) {
    const checkedFlag = [
      ...Array.from(document.querySelectorAll('input[name="medical_care_ids[]"]')),
      this.medicalCareOtherTarget,
    ].some(function (checkbox) {
      return checkbox.checked
    })
    this.chatInputMedicalCareNextButtonTarget.disabled = !checkedFlag
  }

  chatInputMedicalCareNextButtonStatusReset (e) {
    const checkboxes = this.element.parentNode.querySelectorAll('input[type="checkbox"]')
    checkboxes.forEach(checkbox => {
      checkbox.checked = false
    })
    this.chatInputMedicalCareNextButtonStatus()
  }

  chatInputRoomNextButtonStatus (e) {
    const checkedFlag = this.featureContentRoomTargets.some(function (checkbox) {
      return checkbox.checked
    })
    this.chatInputRoomNextButtonTarget.disabled = !checkedFlag
  }

  nextChatWithScrollForMonthlyFeeAndAdmissionFee (e) {
    const monthlyFeeMin = document.getElementById('monthly_fee_min')
    const montlyFeeMinText = monthlyFeeMin.options[monthlyFeeMin.selectedIndex].innerText
    const monthlyFeeMax = document.getElementById('monthly_fee_max')
    const montlyFeeMaxText = monthlyFeeMax.options[monthlyFeeMax.selectedIndex].innerText
    const monthlyFeeText = `月額${montlyFeeMinText}〜${montlyFeeMaxText}、`

    const admissionFeeMin = document.getElementById('admission_fee_min')
    const admissionFeeMinText = admissionFeeMin.options[admissionFeeMin.selectedIndex].innerText
    const admissionFeeMax = document.getElementById('admission_fee_max')
    const admissionFeeMaxText = admissionFeeMax.options[admissionFeeMax.selectedIndex].innerText
    const admissionFeeText = `入居金${admissionFeeMinText}〜${admissionFeeMaxText}でお願いします。`

    this.hideCurrentChatInput(e, monthlyFeeText + admissionFeeText)
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  nextChatWithScrollForMedicalCare (e) {
    const checkboxes = document.querySelectorAll('input[name="medical_care_ids[]"]')
    const medicalCareContent = Array.from(checkboxes).filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.title)
    const medicalCareText = medicalCareContent.length > 0 ? medicalCareContent.join('／') : 'その他（絞り込みなし）'
    this.hideCurrentChatInput(e, medicalCareText + 'です')
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  nextChatWithScrollForFeatureContent (e) {
    const checkboxes = this.featureContentRoomTargets
    const FeatureContent = Array.from(checkboxes).filter((checkbox) => checkbox.checked).map((checkbox) => checkbox.dataset.title)
    const FeatureContentText = FeatureContent.length > 1
      ? FeatureContent.join('もしくは') + 'を希望します。'
      : FeatureContent.length === 1 && FeatureContent[0] !== 'こだわりはありません'
        ? FeatureContent[0] + 'を希望します。'
        : 'こだわりはありません。'
    this.hideCurrentChatInput(e, FeatureContentText)
    this.showNextChat(Number(e.currentTarget.dataset.next))
  }

  toggle (e) {
    const target = e.target.parentNode

    if (target.classList.contains(`${this.className}::accordion-close`)) {
      this.open(target)
    } else {
      this.close(target)
    }
  }

  open (target) {
    target.classList.remove(`${this.className}::accordion-close`)
    target.classList.add(`${this.className}::accordion-open`)
  }

  close (target) {
    target.classList.remove(`${this.className}::accordion-open`)
    target.classList.add(`${this.className}::accordion-close`)
  }

  changeFormValue (e) {
    this.setFacilitiesCount()
  }

  displaySummary (e) {
    this.hideCurrentChatInput(e)
    this.setfacilitiesCountText(Number(e.currentTarget.dataset.next))
    this.setSelectedContentList()
    this.resetButtonTarget.style.display = 'none'
  }

  setSelectedContentList () {
    this.setSelcetedCareLevel()
    this.setSelcetedDementia()
    this.setSelectedWelfare()
    this.setSelectCost()
    // this.changeFeatureContentTagColor()
  }

  setFacilitiesCount (e) {
    if (typeof FormData !== 'function') {
      return
    }

    getCount(this.facilitiesCountParams()).then((count) => {
      const countElement = this.submitTargets[0]
      const startNumber = parseInt(countElement.textContent)
      const endNumber = count.result

      this.submitTargets.forEach((element) => {
        if (count.result >= 10000) {
          element.innerText = '10000+'
        } else if (count.result <= 0) {
          element.innerText = count.result
          window.gtagEvent('step', 'inqueryCategory:area:question', 'form_0_facilities')
        } else {
          element.innerText = count.result
          this.animateCount(countElement, startNumber, endNumber)
        }
      })
    }).catch((error) => {
      // APIエラー時は操作を継続可能にする
      console.error('Failed to fetch facilities count:', error)
    })
  }

  facilitiesCountParams () {
    const formData = new FormData(this.searchQuestionFormTarget)
    const params = {
      city_ids: this.areaIds(document.forms[0].city_ids) || [],
      ordinance_designated_city_ids: this.areaIds(document.forms[0].ordinance_designated_city_ids) || [],
      monthly_fee_min: formData.get('monthly_fee_min'),
      monthly_fee_max: formData.get('monthly_fee_max'),
      admission_fee_min: formData.get('admission_fee_min'),
      admission_fee_max: formData.get('admission_fee_max'),
      care_insurance_rate: formData.get('care_insurance_rate'),
      care_level_id: formData.get('care_level_id'),
      dementia_acceptable: formData.get('dementia_acceptable'),
      medical_care_ids: formData.getAll('medical_care_ids[]').filter(value => value !== ''),
      welfare_acceptable: formData.get('welfare_acceptable'),
      feature_content_ids: formData.getAll('feature_content_ids[]').filter(value => value !== ''),
    }

    return params
  }

  animateCount (element, start, end) {
    let startTime = null

    const updateNumber = (timestamp) => {
      if (!startTime) startTime = timestamp
      const progress = Math.min((timestamp - startTime) / 200, 1)
      const currentNumber = Math.floor(start + (end - start) * progress)
      element.textContent = currentNumber

      if (progress < 1) {
        requestAnimationFrame(updateNumber)
      }
    }

    requestAnimationFrame(updateNumber)
  }

  areaIds (ids) {
    if (!ids) return

    if (Array.from(ids).length > 1) {
      return Array.from(ids).map((id) => id.value)
    } else {
      return Array(ids).map((id) => id.value)
    }
  }

  setfacilitiesCountText (target) {
    getCount(this.facilitiesCountParams()).then((count) => {
      if (count.result > 0) {
        this.facilitiesCountTarget.insertAdjacentHTML('afterbegin', `
          <span>これまで入力いただいた内容で該当する施設は</span>
          <em class="chat-question-em">${count.result}</em>
          <span>件です。</span>
        `)
      } else {
        this.facilitiesCountTarget.insertAdjacentHTML('afterbegin', `
          <span>これまでご入力いただいた内容で該当する施設はございませんでした。</span>
          <br>
          <span>「条件を緩和する」を押して、条件編集ボタンから条件を緩和してみましょう。</span>
        `)
        this.submitButtonTarget.value = '条件を緩和する'
      }
      this.showNextChatSlow(target)
    }).catch((error) => {
      // APIエラー時も次のステップに進めるようにする
      console.error('Failed to fetch facilities count:', error)
      this.showNextChatSlow(target)
    })
  }

  setSelcetedCareLevel () {
    const careLevelIdInput = document.querySelector('input[name="care_level_id"]:checked')
    if (careLevelIdInput) {
      const summaryText = careLevelIdInput.dataset.summaryText
      this.summaryAboutCareLevelTarget.insertAdjacentHTML('afterbegin', `
        <div class="${this.element.dataset.classname}::summary-text">${summaryText}/</div>
      `)
    }
  }

  setSelcetedDementia () {
    const dementiaInput = document.querySelector('input[name="dementia_acceptable"]:checked')
    const summaryText = dementiaInput ? dementiaInput.dataset.summaryText : '認知症なし'
    this.summaryAboutDementiaTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">${summaryText}/</div>
    `)
  }

  setSelectedWelfare () {
    const welfareIdInput = document.querySelector('input[name="welfare_acceptable"]:checked')
    const summaryText = welfareIdInput ? welfareIdInput.dataset.summaryText : '生活保護なし'
    this.summaryAboutWelfareTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">${summaryText}</div>
    `)
  }

  setSelectCost () {
    const monthlyFeeMin = document.getElementById('monthly_fee_min')
    const montlyFeeMinText = monthlyFeeMin.options[monthlyFeeMin.selectedIndex].innerText
    const monthlyFeeMax = document.getElementById('monthly_fee_max')
    const montlyFeeMaxText = monthlyFeeMax.options[monthlyFeeMax.selectedIndex].innerText
    const admissionFeeMin = document.getElementById('admission_fee_min')
    const admissionFeeMinText = admissionFeeMin.options[admissionFeeMin.selectedIndex].innerText
    const admissionFeeMax = document.getElementById('admission_fee_max')
    const admissionFeeMaxText = admissionFeeMax.options[admissionFeeMax.selectedIndex].innerText
    this.summaryAboutMonthlyFeeTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">月額  ${montlyFeeMinText}~${montlyFeeMaxText}</div>
    `)
    this.summaryAboutAdmissionFeeTarget.insertAdjacentHTML('afterbegin', `
      <div class="${this.element.dataset.classname}::summary-text">入居金  ${admissionFeeMinText}~${admissionFeeMaxText}</div>
    `)
  }

  changeFeatureContentTagColor () {
    const featureContentInputs = document.querySelectorAll('input[name="feature_content_ids[]"]:checked')
    const featureContentIds = Array.from(featureContentInputs).map((featureContent) => featureContent.value)
    this.changeNursingTagColor(featureContentIds)
    this.changeRoomTypeTagColor(featureContentIds)
    this.changeRecreationTagColor(featureContentIds)
  }

  changeNursingTagColor (featureContentIds) {
    if (featureContentIds.length === 0) return

    const twentyFourHourNursingId = '103'
    if (featureContentIds.includes(twentyFourHourNursingId)) {
      this.summaryAboutNursingTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutNursingTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    }
  }

  changeRoomTypeTagColor (featureContentIds) {
    const privateRoomId = '201'
    const doubleRoomId = '202'

    if (featureContentIds.includes(privateRoomId)) {
      this.summaryAboutPrivateRoomTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutPrivateRoomTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    } else if (featureContentIds.includes(doubleRoomId)) {
      this.summaryAboutDoubleRoomTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutDoubleRoomTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    } else {
      this.summaryAboutNormalRoomTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutNormalRoomTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    }
  }

  changeRecreationTagColor (featureContentIds) {
    if (featureContentIds.length === 0) return

    const recreationId = '401'
    if (featureContentIds.includes(recreationId)) {
      this.summaryAboutRecreationTarget.classList.remove(`${this.element.dataset.classname}::question-summary-items-tag`)
      this.summaryAboutRecreationTarget.classList.add(`${this.element.dataset.classname}::question-summary-items-tag-active`)
    }
  }
}
